import { baseInputValidationScheme } from '../constants';
import { TContext, TTag } from './types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { TInputState } from '../CustomForm/types';
import { TBaseTagContext } from '../types';
import { validate } from '@krugom/validator';
import { TValidationScheme } from '@krugom/validator/dist/types';

const validationScheme: TValidationScheme = {
  ...baseInputValidationScheme,
  properties: {
    ...baseInputValidationScheme.properties,
    initialValue: {
      type: 'boolean',
    },
  },
};

const CustomInputBoolean: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const result = validate(tagProps, validationScheme);

  const {
    name,
    children,
    required,
    validation,
    initialValue,
    formOnly,
    label,
    description,
    placeholder,
  } = tagProps;

  const formContextInput = name ? context.form?.inputs[name] : undefined;
  const [showHelp, setShowHelp] = useState(false);

  const initialState: TInputState = useMemo(
    () => ({
      name: name || 'unknown-boolean-input',
      label,
      description,
      placeholder,
      type: 'boolean',
      value: !!initialValue,
      errors: [],
      touched: false,
      required,
      validation,
      initialValue,
      formOnly,
      reset: false,
    }),
    [formOnly, initialValue, name, required, validation, label, description, placeholder]
  );

  useEffect(() => {
    if (context.form) {
      if (!formContextInput) {
        context.form.handleSetInput(name, initialState);
      } else if (formContextInput.reset) {
        context.form.handleSetInput(name, initialState);
      }
    }
  }, [context.form, formContextInput, initialState, name]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!context.form) return;
      const inputType = e.target.type;
      const inputValue = e.target.checked;
      if (inputType !== 'checkbox' && inputType !== 'radio') {
        console.warn(`Type ${inputType} is not supported!`);
        return;
      }
      context.form?.handleSetValue(name, inputValue);
    },
    [context.form, name]
  );

  const handleShowHelp = useCallback(() => {
    setShowHelp(prev => !prev);
  }, []);

  const inputContext: TContext = useMemo(
    () => ({
      name,
      type: initialState.type,
      required,
      validation,
      initialValue,
      errors: formContextInput?.errors,
      touched: formContextInput?.touched,
      handleChange,
      handleShowHelp,
      value: !!formContextInput?.value,
      label,
      description,
      showHelp,
    }),
    [
      name,
      initialState.type,
      required,
      validation,
      initialValue,
      formContextInput?.errors,
      formContextInput?.touched,
      handleChange,
      handleShowHelp,
      formContextInput?.value,
      label,
      description,
      showHelp,
    ]
  );

  const newContexts: Partial<TBaseTagContext> = useMemo(
    () => ({
      ...context,
      input: inputContext,
    }),
    [context, inputContext]
  );

  if (!context.form) {
    console.warn('Tag <InputSimpleContext> must be child of the <FormContext> tag!');
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  if (!result.isValid) {
    console.error(
      'Тэг: $InputBooleanContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return null;
  }

  return <UniversalChildren context={newContexts}>{children}</UniversalChildren>;
});
CustomInputBoolean.displayName = 'CustomInputBoolean';
export default CustomInputBoolean;
